import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Percent,
  Scale,
  Banknote,
} from 'lucide-react';

interface ProfitabilityCardProps {
  costs: number;
  revenue: number;
  profit: number;
  costPerKg: number | null;
  revenuePerKg: number | null;
  profitPerKg: number | null;
  roiPct: number | null;
  status: 'profit' | 'loss' | 'no_revenue';
}

function formatPHP(value: number): string {
  return value.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' });
}

export function ProfitabilityCard({
  costs,
  revenue,
  profit,
  costPerKg,
  revenuePerKg,
  profitPerKg,
  roiPct,
  status,
}: ProfitabilityCardProps) {
  // Traffic light colors based on status
  const statusConfig = {
    profit: {
      badgeVariant: 'success' as const,
      badgeLabel: 'Profit',
      profitColor: 'text-emerald-700',
      profitBg: 'bg-emerald-50',
    },
    loss: {
      badgeVariant: 'critical' as const,
      badgeLabel: 'Loss',
      profitColor: 'text-red-700',
      profitBg: 'bg-red-50',
    },
    no_revenue: {
      badgeVariant: 'neutral' as const,
      badgeLabel: 'No Revenue',
      profitColor: 'text-gray-700',
      profitBg: 'bg-gray-50',
    },
  };

  const config = statusConfig[status];

  const kpiItems = [
    {
      label: 'Total Cost',
      value: formatPHP(costs),
      icon: DollarSign,
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600',
    },
    {
      label: 'Revenue',
      value: formatPHP(revenue),
      icon: Banknote,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      label: 'Gross Profit',
      value: formatPHP(profit),
      icon: profit >= 0 ? TrendingUp : TrendingDown,
      iconBg: config.profitBg,
      iconColor: config.profitColor,
      valueColor: config.profitColor,
    },
    {
      label: 'Cost/kg',
      value: costPerKg != null ? formatPHP(costPerKg) : '--',
      icon: Scale,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
    {
      label: 'Revenue/kg',
      value: revenuePerKg != null ? formatPHP(revenuePerKg) : '--',
      icon: BarChart3,
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-600',
    },
    {
      label: 'Profit/kg',
      value: profitPerKg != null ? formatPHP(profitPerKg) : '--',
      icon: profit >= 0 ? TrendingUp : TrendingDown,
      iconBg: profitPerKg != null && profitPerKg >= 0 ? 'bg-emerald-50' : 'bg-red-50',
      iconColor: profitPerKg != null && profitPerKg >= 0 ? 'text-emerald-600' : 'text-red-600',
      valueColor: profitPerKg != null ? (profitPerKg >= 0 ? 'text-emerald-700' : 'text-red-700') : undefined,
    },
    {
      label: 'ROI',
      value: roiPct != null ? `${roiPct.toFixed(1)}%` : '--',
      icon: Percent,
      iconBg: roiPct != null && roiPct >= 0 ? 'bg-emerald-50' : 'bg-red-50',
      iconColor: roiPct != null && roiPct >= 0 ? 'text-emerald-600' : 'text-red-600',
      valueColor: roiPct != null ? (roiPct >= 0 ? 'text-emerald-700' : 'text-red-700') : undefined,
    },
  ];

  return (
    <div className="space-y-3">
      {/* Status badge */}
      <div className="flex items-center gap-2">
        <Badge variant={config.badgeVariant} size="md" dot>
          {config.badgeLabel}
        </Badge>
        {roiPct != null && (
          <span className="text-sm text-gray-500">
            ROI: {roiPct >= 0 ? '+' : ''}{roiPct.toFixed(1)}%
          </span>
        )}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {kpiItems.map((item) => (
          <Card key={item.label}>
            <Card.Body className="flex items-center gap-3 py-3">
              <div className={`rounded-lg p-2 ${item.iconBg} shrink-0`}>
                <item.icon className={`h-4 w-4 ${item.iconColor}`} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">{item.label}</p>
                <p
                  className={`text-base font-semibold truncate ${
                    'valueColor' in item && item.valueColor
                      ? item.valueColor
                      : 'text-gray-900'
                  }`}
                >
                  {item.value}
                </p>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
    </div>
  );
}
